// @flow
import { get, isEmpty } from 'lodash';
import * as React from 'react';
import { bindActionCreators } from 'redux';
import { connect, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import {
    defineMessages, IntlShape, injectIntl, FormattedMessage,
} from 'react-intl';
import type { Match, RouterHistory } from 'react-router-dom';
import ApiClient from 'speed-js-core/src/api/ApiClient';
import type { Confirmation } from 'speed-js-react';
import { connectAPI, withConfirmation } from 'speed-js-react';
import { HTTP_400_BAD_REQUEST } from 'speed-js-core';
import actionMessages from 'esm-web-pro/messages/actionMessages';
import DeleteIcon from 'esm-web-pro/components/layout/sidePanel/icons/DeleteIcon';
import EditIcon from 'esm-web-pro/components/layout/sidePanel/icons/EditIcon';
import TimelineIcon from 'esm-web-pro/components/layout/sidePanel/icons/TimelineIcon';
import MainActions from 'esm-web-pro/components/layout/sidePanel/MainActions';
import SidePane from 'esm-web-pro/components/layout/SidePane';
import * as sidePanelActions from 'esm-web-pro/redux/side-panel/actions';
import type { Cable } from 'esm-web-pro/types/networks';
import BaseEditor, { MAP_OBJECT_KEY } from 'esm-web-pro/containers/map/editor/BaseEditor';
import documentsMessages from 'esm-web-pro/messages/documentsMessages';
import CableDocumentPermitForm from 'esm-web-pro/containers/pages/network/objects/cable/CableDocumentPermitForm';
import sidePanelMessages from 'esm-web-pro/messages/sidePanelMessages';
import PatchCableEditor from './patch/PatchCableEditor';
import CableEditor from './CableEditor';


type SidePanelActionProps = {
    openPanel: (key: string, url: ?string, sensitivity?: boolean) => { key: string, url: string },
    closePanel: (key: string, skipSensitivity: boolean) => { key: string },
}

type Props = {
    ...$Exact<SidePanelActionProps>,
    cable: Cable,
    confirm: (confirmation: Confirmation) => void,
    history: RouterHistory,
    match: Match,
    intl: IntlShape,
    map: Object,
    updateObject: Function,
    deleteObject?: Object,
    mapEditor: BaseEditor,
    editor: Object,
    highlightCable: ?Object,
    onView: Function,
    editor: CableEditor | PatchCableEditor,
}

const msg = defineMessages({
    deleteTitle: {
        id: 'delete-cable-title',
        defaultMessage: 'Are you sure you want to delete the cable?',
    },
    cancelDelete: {
        id: 'delete-cable-cancel',
        defaultMessage: 'Cancel',
    },
    confirmDelete: {
        id: 'delete-cable-confirm',
        defaultMessage: 'Confirm',
    },
    cantDelete: {
        id: 'cant-delete-cable',
        defaultMessage: 'The cable unfortunately cannot be deleted because it is connected to point from layer 1.2.',
    },
});

const CABLE_EDIT_KEY = 'CABLE_EDIT';
const PERMIT_DOCUMENT_KEY = 'POINT1_1_PERMIT_DOCUMENT';

const CableActions = (props: Props) => {
    const {
        cable, history, match, intl: { formatMessage }, map: { is_draft: isDraft, uuid: mapUuid }, confirm,
        updateObject, deleteObject, openPanel, closePanel, mapEditor, highlightCable, onView, editor: EditorComponent,
    } = props;

    const openedPanels = useSelector((state) => state.sidePanel);

    const handleDeleted = (response) => {
        if (response.status === HTTP_400_BAD_REQUEST) {
            confirm({
                message: response.data.error,
                actions: {
                    cancel: {
                        label: formatMessage(msg.cancelDelete),
                        callback: () => {
                        },
                        type: 'negative',
                    },
                },
            });
        } else {
            history.push(match.url.split('/layer')[0]);
            mapEditor._loadFeatures();
        }
    };

    const deleteWithConfirmation = (resourceName: string, resourceArguments: Array<mixed>) => {
        confirm({
            message: formatMessage(msg.deleteTitle),
            actions: {
                cancel: {
                    label: formatMessage(msg.cancelDelete),
                    callback: () => {},
                    type: 'negative',
                },
                confirm: {
                    label: formatMessage(msg.confirmDelete),
                    callback: () => {
                        if (deleteObject) {
                            deleteObject(cable.identity, resourceName, resourceArguments).then(handleDeleted);
                        }
                    },
                    type: 'positive',
                },
            },
        });
    };

    const deleteCable = () => {
        if (cable.patch && isEmpty(cable.connected_fibers)) {
            deleteWithConfirmation('cable.geo', [mapUuid]);
        } else if (cable.start_termination_point || cable.end_termination_point) {
            confirm({
                message: formatMessage(msg.cantDelete),
                actions: {
                    cancel: {
                        label: formatMessage(msg.cancelDelete),
                        callback: () => {
                        },
                        type: 'negative',
                    },
                },
            });
        } else {
            deleteWithConfirmation('cable.geo', [mapUuid]);
        }
    };
    const regex = /^(.+?)(\/+)$/gm;
    const actions = [];

    const cableEditorTitle = (
        <FormattedMessage
            id="cable-editor-title"
            defaultMessage="Edit Cable {id}"
            values={{ id: cable.ref }}
        />
    );

    if (isDraft) {
        actions.push({
            key: `${cable.id}-edit`,
            icon: <EditIcon size="small" />,
            callback: () => openPanel(CABLE_EDIT_KEY, '', match.url, true),
            disabled: mapEditor.state.loading,
            tooltip: formatMessage(actionMessages.edit),
            marginVertical: '15px',
            radius: '0%',
            color: 'transparent',
        }, {
            key: `${cable.id}-add-permit-document`,
            icon: <EditIcon size="small" />,
            callback: () => openPanel(
                PERMIT_DOCUMENT_KEY,
                formatMessage(sidePanelMessages.permitDocument),
                match.url,
                true,
            ),
            disabled: mapEditor.state.loading,
            tooltip: formatMessage(documentsMessages.addNewPermit),
            marginVertical: '15px',
            radius: '0%',
            color: 'transparent',
        });
    }

    actions.push({
        key: `${cable.id}-timeline`,
        icon: <TimelineIcon />,
        tooltip: formatMessage(actionMessages.timeline),
        callback: () => history.push(`${match.url.replace(regex, '$1')}/timeline/cable/${cable.identity}`),
        marginVertical: '15px',
        radius: '0%',
        color: 'transparent',
    });

    if (isDraft) {
        actions.push({
            key: `${cable.id}-delete`,
            icon: <DeleteIcon size="small" />,
            callback: deleteCable,
            disabled: mapEditor.state.loading,
            tooltip: formatMessage(actionMessages.delete),
            marginVertical: '15px',
            radius: '0%',
            color: 'transparent',
        });
    }

    const handleClose = (skipSensitivity = false, panel = CABLE_EDIT_KEY) => {
        closePanel(panel, skipSensitivity);

        if (openedPanels[MAP_OBJECT_KEY]) {
            onView();
        }
    };

    const handleSave = (values, panel = CABLE_EDIT_KEY) => {
        handleClose(true, panel);
        updateObject(values);
    };

    const handleCableDocumentPermitFormClose = (skipSensitivity = false) => {
        handleClose(skipSensitivity, PERMIT_DOCUMENT_KEY);
    };

    const handleCableDocumentPermitFormSave = (values) => {
        handleSave(values, PERMIT_DOCUMENT_KEY);
    };

    return (
        <>
            <SidePane
                id={CABLE_EDIT_KEY}
                key={cable.identity}
                title={cableEditorTitle}
            >
                <EditorComponent
                    id={CABLE_EDIT_KEY}
                    onClose={handleClose}
                    onSave={handleSave}
                    cable={cable}
                    mapUuid={mapUuid}
                    editor={mapEditor}
                    highlightCable={highlightCable}
                    onView={onView}
                />
            </SidePane>
            <SidePane
                id={PERMIT_DOCUMENT_KEY}
                key="permit-document"
                title={formatMessage(sidePanelMessages.permitDocument)}
            >
                <CableDocumentPermitForm
                    cable={cable}
                    onClose={handleCableDocumentPermitFormClose}
                    onSave={handleCableDocumentPermitFormSave}
                />
            </SidePane>
            <MainActions main actions={actions} />
        </>
    );
};

CableActions.defaultProps = {
    deleteObject: undefined,
};

const apiCallback = (apiClient: ApiClient) => ({
    deleteObject: {
        send: (id, resourceName, resourceArguments = []) => {
            const resource = get(apiClient.resources, resourceName);
            return resource.delete(...resourceArguments, id);
        },
    },
});

const mapDispatchToProps = (dispatch) => (
    bindActionCreators(sidePanelActions, dispatch)
);

export default connectAPI(apiCallback)(
    withRouter(
        withConfirmation(
            injectIntl(
                connect(null, mapDispatchToProps)(CableActions),
            ),
        ),
    ),
);
